import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp } from "lucide-react";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { TxTitle, useArticlePath } from "@/lib/i18n";
import { homeTrack, CardCTA } from "./shared";

const CAT_META: Record<string, { label: string; color: string }> = {
  crypto: { label: "Crypto", color: "#F7931A" },
  defi: { label: "DeFi", color: "#00E5FF" },
  web3: { label: "Web3", color: "#A78BFA" },
  blockchain: { label: "Chain", color: "#818CF8" },
  markets: { label: "Markets", color: "#22C55E" },
  "government-policy": { label: "Reg", color: "#60A5FA" },
  research: { label: "Research", color: "#F472B6" },
  explainers: { label: "Learn", color: "#FBBF24" },
};

const hhmm = (iso?: string) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  } catch {
    return "";
  }
};

export const Newsstream = () => {
  const qc = useQueryClient();
  const toArticle = useArticlePath();
  const { data } = useQuery({
    queryKey: ["newsstream"],
    queryFn: async () => (await api.get("/articles?limit=6")).data,
    staleTime: 60 * 1000,
  });
  const items: Article[] = data?.items || [];
  const baseline = items[0]?.published_at;
  const { data: since } = useQuery({
    queryKey: ["newsstream-since", baseline],
    queryFn: async () => (await api.get(`/articles-since?ts=${encodeURIComponent(baseline!)}`)).data,
    enabled: !!baseline,
    refetchInterval: 60000,
  });
  const fresh = since?.count || 0;

  return (
    <div data-testid="newsstream" className="rounded-xl p-4 sm:p-5 flex flex-col lg:flex-1"
      style={{
        border: "1px solid transparent",
        background: "linear-gradient(#000000,#000000) padding-box, linear-gradient(180deg, rgba(122,46,245,0) 0%, #7333e5 100%) border-box",
      }}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#ab6bff]">Newsstream</p>
        <span className="inline-flex items-center gap-1.5 rounded border border-[#0dd480]/40 bg-[#0dd480]/[0.07] px-1.5 py-0.5">
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22C55E] opacity-60" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#22C55E]" />
          </span>
          <span className="font-mono text-[8px] font-bold uppercase tracking-widest text-[#0dd480]">Live</span>
        </span>
      </div>
      {fresh > 0 && (
        <button type="button" data-testid="newsstream-new-updates"
          onClick={() => qc.invalidateQueries({ queryKey: ["newsstream"] })}
          className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#7C3AED]/60 bg-[#7C3AED]/10 text-[#A78BFA] hover:text-white hover:border-[#7C3AED] font-mono text-[11px] py-1.5 px-3 transition-colors">
          <ArrowUp className="h-3 w-3" />
          {fresh} new update{fresh > 1 ? "s" : ""} — tap to load
        </button>
      )}
      <div className="mt-1.5 flex-1">
        {items.slice(0, 5).map((a) => {
          const meta = a.is_breaking
            ? { label: "Breaking", color: "#EF4444" }
            : CAT_META[a.categories?.[0] || ""] || null;
          return (
            <Link key={a.slug} to={toArticle(a.slug)} data-testid={`newsstream-item-${a.slug}`}
              onClick={() => homeTrack("newsstream", a.slug)}
              className="group flex gap-3 items-start py-3 border-b border-[#1c212e] last:border-0">
              <span className="flex flex-col items-start shrink-0 w-[46px] pt-0.5">
                <span className="font-mono text-[10px] text-[#7a8299]">{hhmm(a.published_at)}</span>
                {meta && (
                  <span className="flex items-center gap-1 mt-1">
                    <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />
                    <span className="font-mono text-[8px] uppercase tracking-wider" style={{ color: meta.color, opacity: 0.85 }}>
                      {meta.label}
                    </span>
                  </span>
                )}
              </span>
              <span className="font-news text-[14px] font-semibold text-[#f2f5fc] group-hover:text-[#0dd6eb] transition-colors leading-snug line-clamp-2 min-w-0">
                <TxTitle slug={a.slug} title={a.title} />
              </span>
            </Link>
          );
        })}
        {items.length === 0 && (
          <p className="text-sm text-gray-500 py-4">Fresh stories are on the way.</p>
        )}
      </div>
      <CardCTA to="/latest" testid="newsstream-view-all" module="newsstream">View all news</CardCTA>
    </div>
  );
};
